import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { reportFormError } from '@/lib/errorReporting';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { RegistroPago } from '@/services/paymentService';
import { toast } from 'sonner';

interface PaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payment: RegistroPago) => void;
  initialData?: RegistroPago | null;
  debtId: string;
}

const formSchema = z.object({
  monto_pagado: z.number().positive({ message: "El monto pagado debe ser un número positivo." }),
  metodo_pago: z.string().min(1, { message: "El método de pago es requerido." }),
  referencia_pago: z.string().optional(),
  notas: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const PaymentForm: React.FC<PaymentFormProps> = ({ isOpen, onClose, onSave, initialData, debtId }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      monto_pagado: initialData?.monto_pagado || 0,
      metodo_pago: initialData?.metodo_pago || "",
      referencia_pago: initialData?.referencia_pago || "",
      notas: initialData?.notas || "",
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        monto_pagado: initialData.monto_pagado || 0,
        metodo_pago: initialData.metodo_pago || "",
        referencia_pago: initialData.referencia_pago || "",
        notas: initialData.notas || "",
      });
    } else {
      form.reset();
    }
  }, [initialData, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      onSave({
        ...initialData,
        id_deuda: debtId,
        fecha_pago: new Date().toISOString(),
        monto_pagado: values.monto_pagado,
        metodo_pago: values.metodo_pago,
        referencia_pago: values.referencia_pago || undefined,
        notas: values.notas || undefined,
      });
      
      onClose();
      toast.success(initialData ? "Pago actualizado exitosamente." : "Pago registrado exitosamente.");
    } catch (error) {
      reportFormError(error, values, {
        component: 'PaymentForm',
        action: initialData ? 'update' : 'create',
        recordId: initialData?.id_registro_pago,
        additionalContext: {
          debtId,
          amount: values.monto_pagado,
          paymentMethod: values.metodo_pago
        }
      });
      toast.error("Error al procesar el pago");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData ? "Editar Pago" : "Registrar Nuevo Pago"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="monto_pagado"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Monto Pagado</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="0.00" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="metodo_pago"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Método de Pago</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un método" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Transferencia">Transferencia</SelectItem>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="referencia_pago"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referencia de Pago</FormLabel>
                  <FormControl>
                    <Input placeholder="Número de transacción, cheque, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Input placeholder="Comentarios adicionales" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full">Registrar Pago</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentForm;
